import React from 'react'
import { TFunction } from 'react-i18next'
import {
  Avatar,
  Button,
  DataTableCellVariants,
  DataTableRowData,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { PreProcessedPayouts } from 'pages/MassPayouts/types'
import { PaymentStatus } from 'types/payments'
import { getMassPayoutStatusType, titleCase } from 'utils/utils'

interface FiatTableDataProps {
  massPayoutPreProcessedPayouts: PreProcessedPayouts[]
  isFiatPayoutSubmitted: boolean
  handleShowEditFiatPayoutModal: (
    event: React.MouseEvent<HTMLButtonElement>,
    payout: PreProcessedPayouts
  ) => void
  onOpenPayoutDetails: (payoutIndex?: number) => void
  handleDeleteFiatPayout: (
    event: React.MouseEvent<HTMLButtonElement>,
    payout: PreProcessedPayouts
  ) => void
  isDeleting: boolean
  activeRecord: string
  t: TFunction<'translation', undefined>
  darkMode?: boolean
}

export const getFiatBatchTableData = ({
  massPayoutPreProcessedPayouts,
  isFiatPayoutSubmitted,
  handleShowEditFiatPayoutModal,
  onOpenPayoutDetails,
  handleDeleteFiatPayout,
  isDeleting,
  activeRecord,
  t,
  darkMode,
}: FiatTableDataProps): DataTableRowData[] => {
  if (!massPayoutPreProcessedPayouts) return []

  return massPayoutPreProcessedPayouts?.map((payout, index) => {
    const isAmountValid =
      payout?.amount?.value != null &&
      payout?.amount?.value !== '' &&
      !Number.isNaN(Number(payout?.amount?.value))

    return {
      id: payout?.reference,
      testid: payout?.reference,
      onRowClick: () => {
        onOpenPayoutDetails(index)
      },
      cells: [
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: isAmountValid ? `${payout?.amount?.value} ${payout?.amount?.currency}` : '',
        },
        {
          key: 'recipient',
          variant: DataTableCellVariants.TEXT,
          Avatar: <Avatar name={payout?.amount?.currency} darkMode={darkMode} />,
          Text:
            payout?.details?.beneficiary?.entity?.name ||
            `${payout?.details?.beneficiary?.entity?.firstName} ${payout?.details?.beneficiary?.entity?.lastName}`,
          BottomText: payout?.details?.beneficiary?.bankAccount?.accountNumber,
        },
        {
          key: 'paymentReference',
          variant: DataTableCellVariants.TEXT,
          Text: payout?.paymentReference,
        },
        {
          key: 'status',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights:
            payout?.status === PaymentStatus.VALID
              ? [
                  <Highlight
                    key={`${payout?.reference}-status`}
                    type="success"
                    variant="emphasis"
                    iconElement={<Icon name="Check" />}
                  >
                    {t('massPayouts.fixed')}
                  </Highlight>,
                ]
              : [
                  <Highlight
                    key={`${payout?.reference}-status`}
                    type={getMassPayoutStatusType(payout?.status?.toUpperCase())}
                  >
                    {titleCase(payout?.status)}
                  </Highlight>,
                ],
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            ...(payout?.status !== PaymentStatus.VALID && payout?.status !== PaymentStatus.CREATED
              ? [
                  <Button
                    key={`edit-${payout?.reference}-edit-button`}
                    iconElement={<Icon name="SimplePencilCustom" />}
                    variant="ghost"
                    onClick={e => handleShowEditFiatPayoutModal(e, payout)}
                  />,
                  <Button
                    key={`delete-${payout?.reference}-delete-button`}
                    iconElement={<Icon name="Trash" />}
                    variant="ghost"
                    onClick={e => handleDeleteFiatPayout(e, payout)}
                    loading={activeRecord === payout?.reference && isDeleting}
                  />,
                ]
              : []),
          ],
        },
      ],
    }
  })
}
